/**
 * Utilitaires de formatage pour le centre financier
 */

/**
 * Formate un montant en devise locale (Euro)
 * @param {number} amount - Montant à formater
 * @param {string} currency - Code devise (default: 'EUR')
 * @param {string} locale - Locale (default: 'fr-FR')
 * @returns {string} Montant formaté
 */
export const formatCurrency = (amount, currency = 'EUR', locale = 'fr-FR') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0,00 €';
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    // Fallback en cas d'erreur
    return `${parseFloat(amount).toFixed(2)} €`;
  }
};

/**
 * Formate une date au format local français
 * @param {string|Date} date - Date à formater
 * @param {Object} options - Options de formatage
 * @returns {string} Date formatée
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '-';

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('fr-FR', defaultOptions).format(dateObj);
  } catch (error) {
    return '-';
  }
};

/**
 * Formate une date et heure
 * @param {string|Date} date - Date à formater
 * @returns {string} Date et heure formatées
 */
export const formatDateTime = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formate une date courte (JJ/MM/AAAA)
 * @param {string|Date} date - Date à formater
 * @returns {string} Date courte formatée
 */
export const formatDateShort = (date) => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    return dateObj.toLocaleDateString('fr-FR');
  } catch (error) {
    return '-';
  }
};

/**
 * Formate une date au format universel DD/MM/YYYY (indépendant de la langue)
 * @param {string|Date} date - Date à formater
 * @returns {string} Date formatée au format DD/MM/YYYY
 */
export const formatDateUniversal = (date) => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Formate une date et heure au format universel DD/MM/YYYY HH:MM (indépendant de la langue)
 * @param {string|Date} date - Date à formater
 * @returns {string} Date et heure formatées au format DD/MM/YYYY HH:MM
 */
export const formatDateTimeUniversal = (date) => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Formate un pourcentage
 * @param {number} value - Valeur à formater (0-1 ou 0-100)
 * @param {boolean} isDecimal - Si la valeur est déjà en décimal (0-1)
 * @returns {string} Pourcentage formaté
 */
export const formatPercentage = (value, isDecimal = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(1)}%`;
};

/**
 * Formate un numéro de transaction/référence
 * @param {string} reference - Référence à formater
 * @returns {string} Référence formatée
 */
export const formatReference = (reference) => {
  if (!reference) return '-';
  
  // Format: XXXX-XXXX-XXXX
  const clean = reference.replace(/[^A-Za-z0-9]/g, '');
  if (clean.length >= 12) {
    return `${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8, 12)}`;
  }
  return reference;
};

/**
 * Formate un statut avec couleur
 * @param {string} status - Statut à formater
 * @param {Object} statusLabels - Mapping statut -> label
 * @param {Object} statusColors - Mapping statut -> couleur
 * @returns {Object} {label, color}
 */
export const formatStatus = (status, statusLabels = {}, statusColors = {}) => {
  const label = statusLabels[status] || status || 'Inconnu';
  const color = statusColors[status] || 'default';
  
  return { label, color };
};

/**
 * Formate une durée en texte lisible
 * @param {number} minutes - Durée en minutes
 * @returns {string} Durée formatée
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '-';

  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = minutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}j`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}min`);

  return parts.join(' ') || '0min';
};

/**
 * Formate une période (date début - date fin)
 * @param {string|Date} startDate - Date de début
 * @param {string|Date} endDate - Date de fin
 * @returns {string} Période formatée
 */
export const formatPeriod = (startDate, endDate) => {
  const start = formatDateShort(startDate);
  const end = formatDateShort(endDate);
  
  if (start === '-' || end === '-') return '-';
  
  return `${start} - ${end}`;
};

/**
 * Calcule et formate la différence entre deux montants
 * @param {number} current - Montant actuel
 * @param {number} previous - Montant précédent
 * @returns {Object} {value, percentage, isPositive}
 */
export const formatDifference = (current, previous) => {
  if (!previous || previous === 0) {
    return {
      value: formatCurrency(current),
      percentage: current > 0 ? '+100%' : '0%',
      isPositive: current >= 0
    };
  }

  const difference = current - previous;
  const percentage = (difference / previous) * 100;
  
  return {
    value: formatCurrency(Math.abs(difference)),
    percentage: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`,
    isPositive: difference >= 0
  };
};

/**
 * Tronque un texte avec ellipses
 * @param {string} text - Texte à tronquer
 * @param {number} maxLength - Longueur maximale
 * @returns {string} Texte tronqué
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text || '';
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Validation et formatage d'un IBAN
 * @param {string} iban - IBAN à formater
 * @returns {string} IBAN formaté
 */
export const formatIBAN = (iban) => {
  if (!iban) return '';
  
  // Nettoie l'IBAN
  const clean = iban.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  
  // Formate par groupes de 4
  const formatted = clean.replace(/(.{4})/g, '$1 ').trim();
  
  return formatted;
};

/**
 * Valide un IBAN basique (longueur et format)
 * @param {string} iban - IBAN à valider
 * @returns {boolean} Validité de l'IBAN
 */
export const validateIBAN = (iban) => {
  if (!iban) return false;
  
  const clean = iban.replace(/[^A-Za-z0-9]/g, '');
  
  // Vérifie la longueur (entre 15 et 34 caractères)
  if (clean.length < 15 || clean.length > 34) return false;
  
  // Vérifie que ça commence par 2 lettres
  const countryCode = clean.slice(0, 2);
  if (!/^[A-Z]{2}$/.test(countryCode)) return false;
  
  return true;
};

/**
 * Formate un numéro de téléphone
 * @param {string} phone - Numéro à formater
 * @returns {string} Numéro formaté
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  const clean = phone.replace(/[^0-9+]/g, '');
  
  // Format français
  if (clean.length === 10 && clean.startsWith('0')) {
    return clean.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  
  return clean;
};

// Constantes utiles
export const CURRENCY_SYMBOL = '€';
export const DATE_FORMAT_OPTIONS = {
  short: { year: 'numeric', month: '2-digit', day: '2-digit' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  withTime: { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
};
